import { ArtycAIAction } from '../../api/artycAiApi';
import { ChatMessage } from './components/ChatPanel';

const KEY_PREFIX = 'artyc.artycAI.thread.v1.';
const MAX_MESSAGES = 40;
const OBJECT_ID = /^[a-f\d]{24}$/i;

const keyFor = (userId: string) => `${KEY_PREFIX}${userId}`;

const isChatMessage = (value: unknown): value is ChatMessage => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    (candidate.author === 'user' || candidate.author === 'assistant') &&
    typeof candidate.prose === 'string'
  );
};

const isObjectId = (value: unknown) =>
  typeof value === 'string' && OBJECT_ID.test(value);

const ACTION_FIELD_CHECKS: Record<
  ArtycAIAction['type'],
  (candidate: Record<string, unknown>) => boolean
> = {
  SHOW_JOURNEYS: (candidate) =>
    ['ACT', 'WATCH', 'IGNORE'].includes(candidate.level as string),
  SHOW_JOURNEY: (candidate) =>
    typeof candidate.journeyPid === 'string' && isObjectId(candidate.journeyId),
  SHOW_SHIPPER: (candidate) => typeof candidate.serialNumber === 'string',
  SHOW_EVENT: (candidate) =>
    typeof candidate.eventPid === 'string' && isObjectId(candidate.eventId),
  SHOW_EVENTS: (candidate) =>
    typeof candidate.companyName === 'string' &&
    Array.isArray(candidate.companyIds) &&
    candidate.companyIds.length > 0 &&
    candidate.companyIds.every(isObjectId),
};

const isChatAction = (value: unknown): value is ArtycAIAction => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.label !== 'string') return false;
  if (
    typeof candidate.type !== 'string' ||
    !Object.prototype.hasOwnProperty.call(ACTION_FIELD_CHECKS, candidate.type)
  ) {
    return false;
  }
  return ACTION_FIELD_CHECKS[candidate.type as ArtycAIAction['type']](
    candidate
  );
};

const withValidActions = (message: ChatMessage): ChatMessage =>
  message.actions === undefined
    ? message
    : {
        ...message,
        actions: Array.isArray(message.actions)
          ? message.actions.filter(isChatAction)
          : [],
      };

export const readThread = (userId: string): ChatMessage[] => {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isChatMessage).map(withValidActions);
  } catch {
    return [];
  }
};

export const writeThread = (userId: string, messages: ChatMessage[]): void => {
  try {
    const capped = messages.slice(-MAX_MESSAGES);
    localStorage.setItem(keyFor(userId), JSON.stringify(capped));
  } catch {
    /* ignore */
  }
};

export const clearThread = (userId: string): void => {
  try {
    localStorage.removeItem(keyFor(userId));
  } catch {
    /* ignore */
  }
};

export const clearAllThreads = (): void => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
};
